/**
 * AnimatedDataTable Component
 *
 * A simplified table component that supports cell-level highlighting
 * and smooth transitions between data states.
 */

import React, { useMemo, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';

export type HighlightState = 'none' | 'analyzing' | 'complete';

export interface AnimatedDataTableProps {
  data: string[][];
  highlightedColumns?: number[];
  highlightState?: HighlightState;
  isTransitioning?: boolean;
  maxRows?: number;
  className?: string;
  title?: string;
}

export function AnimatedDataTable({
  data,
  highlightedColumns = [],
  highlightState = 'none',
  isTransitioning = false,
  maxRows = 15,
  className,
  title,
}: AnimatedDataTableProps) {
  const tableRef = useRef<HTMLDivElement>(null);

  // Separate headers from data rows
  const headers = data[0] || [];
  const rows = data.slice(1, maxRows + 1);
  const totalRows = data.length - 1;
  const showingAll = totalRows <= maxRows;

  // Scroll to top when data changes
  useEffect(() => {
    if (tableRef.current) {
      tableRef.current.scrollTop = 0;
    }
  }, [data]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className={cn(
        'rounded-lg border bg-white shadow-sm overflow-hidden',
        className,
      )}
    >
      {title && (
        <div className="border-b bg-gray-50 px-4 py-2">
          <h4 className="text-sm font-medium text-gray-700">{title}</h4>
        </div>
      )}
      <div ref={tableRef} className="overflow-auto max-h-[400px]">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 sticky top-0">
            <tr>
              {headers.map((header, colIndex) => {
                const isHighlighted = highlightedColumns.includes(colIndex);
                return (
                  <th
                    key={`header-${colIndex}`}
                    className={cn(
                      'px-3 py-2 text-left font-medium border-b whitespace-nowrap transition-colors duration-500',
                      !isHighlighted && 'text-gray-700',
                      isHighlighted &&
                        highlightState === 'analyzing' &&
                        'bg-yellow-100 text-yellow-800',
                      isHighlighted &&
                        highlightState === 'complete' &&
                        'bg-green-100 text-green-800',
                    )}
                  >
                    {header}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            <AnimatePresence mode="wait">
              {rows.map((row, rowIndex) => (
                <motion.tr
                  key={`row-${rowIndex}`}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: rowIndex * 0.02 }}
                  className={cn(
                    'border-b hover:bg-gray-50',
                    rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50/50',
                  )}
                >
                  {row.map((cell, colIndex) => (
                    <AnimatedCell
                      key={`cell-${rowIndex}-${colIndex}`}
                      value={cell}
                      isHighlighted={highlightedColumns.includes(colIndex)}
                      highlightState={highlightState}
                      isTransitioning={isTransitioning}
                    />
                  ))}
                </motion.tr>
              ))}
            </AnimatePresence>
          </tbody>
        </table>
      </div>
      {!showingAll && (
        <div className="border-t bg-gray-50 px-4 py-2 text-center text-xs text-gray-500">
          Showing {rows.length} of {totalRows} rows
        </div>
      )}
    </motion.div>
  );
}

/**
 * AnimatedCell Component
 * Individual cell with highlight animation based on state
 */
interface AnimatedCellProps {
  value: string;
  isHighlighted: boolean;
  highlightState: HighlightState;
  isTransitioning: boolean;
}

function AnimatedCell({
  value,
  isHighlighted,
  highlightState,
  isTransitioning,
}: AnimatedCellProps) {
  // Determine background color based on highlight state
  const getBackgroundClass = () => {
    if (!isHighlighted) return '';
    if (highlightState === 'analyzing') return 'bg-yellow-50';
    if (highlightState === 'complete') return 'bg-green-50';
    return '';
  };

  return (
    <motion.td
      className={cn(
        'px-3 py-2 whitespace-nowrap transition-colors duration-500',
        getBackgroundClass(),
      )}
      animate={
        isHighlighted && isTransitioning && highlightState === 'complete'
          ? {
              backgroundColor: [
                'rgba(254, 240, 138, 0.8)',
                'rgba(134, 239, 172, 0.5)',
                'rgba(134, 239, 172, 0.2)',
              ],
            }
          : {}
      }
      transition={{ duration: 1.2 }}
    >
      <motion.span
        key={value}
        initial={
          isHighlighted && isTransitioning ? { opacity: 0, y: 5 } : false
        }
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {value}
      </motion.span>
    </motion.td>
  );
}

/**
 * DataTransitionWrapper Component
 * Wraps the table and handles smooth transitions between data states
 */
export interface DataTransitionWrapperProps {
  children: React.ReactNode;
  dataKey: string;
}

export function DataTransitionWrapper({
  children,
  dataKey,
}: DataTransitionWrapperProps) {
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={dataKey}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.4, ease: 'easeInOut' }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}

/**
 * CompactDataPreview Component
 * Shows a smaller preview of the data with key columns highlighted
 */
export interface CompactDataPreviewProps {
  data: string[][];
  highlightedColumns?: number[];
  maxCols?: number;
  maxRows?: number;
  className?: string;
}

export function CompactDataPreview({
  data,
  highlightedColumns = [],
  maxCols = 6,
  maxRows = 8,
  className,
}: CompactDataPreviewProps) {
  const headers = data[0]?.slice(0, maxCols) || [];
  const rows = data.slice(1, maxRows + 1).map((row) => row.slice(0, maxCols));

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className={cn(
        'rounded-lg border bg-white/80 shadow-sm overflow-hidden text-xs',
        className,
      )}
    >
      <table className="w-full">
        <thead className="bg-gray-100">
          <tr>
            {headers.map((header, i) => (
              <th
                key={i}
                className={cn(
                  'px-2 py-1.5 text-left font-medium text-gray-600 border-b',
                  highlightedColumns.includes(i) &&
                    'bg-yellow-100 text-yellow-800',
                )}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr
              key={rowIndex}
              className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
            >
              {row.map((cell, colIndex) => (
                <td
                  key={colIndex}
                  className={cn(
                    'px-2 py-1 border-b border-gray-100',
                    highlightedColumns.includes(colIndex) &&
                      'bg-yellow-50 font-medium',
                  )}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </motion.div>
  );
}
